/**
 * WHAT A PAGE ADDS TO THE SITE'S DRAWER.
 *
 * The drawer is the site's, but some pages own part of the person's story: on
 * the women's conference page it is her ticket and her picks. Those widgets
 * are separate bundles, so the contract is the window:
 *
 *   window.__LOGIN_DRAWER_SECTIONS__ = { [id]: DrawerSection }
 *   window.dispatchEvent(new Event('login-drawer:sections'))   // after any change
 *
 * A tap on a row closes the drawer and hands the row back to the page:
 *
 *   window.addEventListener('login-drawer:select', (e) => e.detail // { section, row })
 *
 * Plain data only, so nothing either side renders crosses a bundle (or a
 * shadow root). A section with no rows is not drawn.
 */
import { useEffect, useState } from 'react';

export interface DrawerRow {
  id: string;
  title: string;
  subtitle?: string;
  /** A face or a thumbnail; a person's name gives initials when it is missing. */
  imageUrl?: string | null;
  /** The person the row is about, for the initials fallback. */
  personName?: string;
}

/**
 * How a page's card may look in the drawer, as plain values. Optional: a
 * section without one wears the drawer's own card. Every value is checked
 * before it touches a style (hex colours, https art, a plain font list), so a
 * page cannot put arbitrary CSS into the site's drawer.
 */
export interface DrawerAppearance {
  /** The card's ground colour, under the image. */
  background: string;
  /** A painted ground, cover-fitted, with `scrim` laid over it for the text. */
  image?: string;
  scrim?: string;
  /** Names and chevrons. */
  ink: string;
  /** The line under each name. */
  muted: string;
  /** Hairlines between rows. */
  divider?: string;
  /** The name's face, e.g. "'Cormorant Garamond', Georgia, serif". Already loaded by the page. */
  displayFont?: string;
  /** Art tucked into the card's bottom-right corner. */
  cornerArt?: string;
}

export interface DrawerSection {
  id: string;
  appearance?: DrawerAppearance;
  /** The small caps over the card: "What Matters". */
  kicker: string;
  rows: DrawerRow[];
  /** Lower first; sections without one follow in the order they arrived. */
  order?: number;
}

declare global {
  interface Window {
    __LOGIN_DRAWER_SECTIONS__?: Record<string, DrawerSection>;
  }
}

export const SECTIONS_EVENT = 'login-drawer:sections';
export const SELECT_EVENT = 'login-drawer:select';

function read(): DrawerSection[] {
  const all = Object.values(window.__LOGIN_DRAWER_SECTIONS__ ?? {});
  return all
    .filter((s) => s && Array.isArray(s.rows) && s.rows.length > 0)
    .sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
}

export function useDrawerSections(): DrawerSection[] {
  const [sections, setSections] = useState<DrawerSection[]>([]);
  useEffect(() => {
    const update = () => setSections(read());
    update();
    window.addEventListener(SECTIONS_EVENT, update);
    return () => window.removeEventListener(SECTIONS_EVENT, update);
  }, []);
  return sections;
}

const HEX = /^#[0-9a-f]{6}$/i;
const FONT = /^[\w\s,'"-]{1,120}$/;

function safeUrl(value: string | undefined): string | null {
  if (!value) return null;
  try {
    const url = new URL(value, window.location.href);
    if (url.protocol !== 'https:' && !/^(localhost|127\.0\.0\.1)$/.test(url.hostname)) return null;
    if (/["'\\()\s]/.test(url.href)) return null;
    return url.href;
  } catch {
    return null;
  }
}

/** The appearance with anything unsafe dropped, or null when the basics fail. */
export function checkedAppearance(a: DrawerAppearance | undefined): DrawerAppearance | null {
  if (!a || !HEX.test(a.background) || !HEX.test(a.ink) || !HEX.test(a.muted)) return null;
  return {
    background: a.background,
    ink: a.ink,
    muted: a.muted,
    scrim: a.scrim && HEX.test(a.scrim) ? a.scrim : undefined,
    divider: a.divider && HEX.test(a.divider) ? a.divider : undefined,
    displayFont: a.displayFont && FONT.test(a.displayFont) ? a.displayFont : undefined,
    image: safeUrl(a.image) ?? undefined,
    cornerArt: safeUrl(a.cornerArt) ?? undefined,
  };
}

export function selectRow(section: string, row: string) {
  window.dispatchEvent(new CustomEvent(SELECT_EVENT, { detail: { section, row } }));
}
